<template>
  <div v-if="!hideCard && (riskMetricData || riskMetricLoading || riskMetricError)">
    <!-- Risk Score Card -->
    <div class="risk-metric-card shadow-lg mb-4">
      <div class="risk-metric-header">
        <div class="d-flex align-items-center justify-content-between">
          <h3 class="risk-metric-title flex-grow-1">Entity cumulative risk score</h3>
          <button v-if="!riskMetricLoading" @click="fetchRiskMetricQuery" class="refresh-btn" title="Refresh risk score"
            :disabled="riskMetricLoading">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>
      <div class="risk-metric-body">
        <!-- Success state -->
        <div v-if="riskMetricData" class="risk-metric-content">
          <div class="risk-score-display">
            <span class="risk-score-value">{{ riskMetricData.result }}</span>
            <span class="risk-score-separator">/</span>
            <span class="risk-score-max">{{ riskMetricData.max_possible_score }}</span>
          </div>

          <!-- Informational block -->
          <div class="risk-metric-info">
            <i class="fas fa-info-circle me-2"></i>
            <span>This score is calculated using a custom scoring algorithm that was applied on your behalf. For more
              information about this algorithm, or to report any issues with the scoring, please contact your system
              administrator.</span>
          </div>
        </div>

        <!-- Loading state -->
        <div v-else-if="riskMetricLoading" class="risk-metric-loading">
          <div class="spinner-border text-primary mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mb-0">Loading risk score...</p>
        </div>

        <!-- Error state -->
        <div v-else-if="riskMetricError" class="risk-metric-error">
          <i class="fas fa-exclamation-triangle text-warning mb-2"></i>
          <p class="text-warning mb-0">{{ riskMetricError }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";

export default {
  name: "CumulativeRiskScore",
  props: {
    entityId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      riskMetricData: null,
      riskMetricLoading: false,
      riskMetricError: null,
      hideCard: false,
    };
  },
  methods: {
    async fetchRiskMetricQuery() {
      if (!this.entityId) {
        return;
      }
      this.riskMetricLoading = true;
      this.riskMetricError = null;
      this.riskMetricData = null;
      this.hideCard = false;
      try {
        const TOKEN = localStorage.getItem('token');
        if (!TOKEN) throw new Error('Missing auth token');
        const url = `${API_BASE_URL}/verify_api/workflow_management_api/execute_risk_metric_query/`;
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${TOKEN}`,
          },
          body: JSON.stringify({
            entity_id: this.entityId,
          }),
        });

        if ([400, 404].includes(resp.status)) {
          // Hide the card for 404 and 400 errors
          this.hideCard = true;
          return;
        }

        if (!resp.ok) {
          throw new Error(`Failed to fetch risk metric query (${resp.status})`);
        }
        const payload = await resp.json();
        if (payload.status === 'success' && payload.data) {
          this.riskMetricData = payload.data;
        } else {
          throw new Error('Invalid response format');
        }
      } catch (e) {
        console.error('Risk metric query fetch error', e);
        this.riskMetricError = e.message || String(e);
      } finally {
        this.riskMetricLoading = false;
      }
    },
  },
  watch: {
    entityId: {
      handler() {
        this.fetchRiskMetricQuery();
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
/* Risk Metric Card Styles */
.risk-metric-card {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.risk-metric-card:hover {
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.16);
  transform: translateY(-2px);
}

.risk-metric-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1.5rem 2rem;
  color: white;
}

.risk-metric-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
  text-align: center;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

.refresh-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-btn i {
  font-size: 0.875rem;
}

.risk-metric-body {
  padding: 2.5rem 2rem;
}

.risk-metric-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

/* Risk Score Display Styles */
.risk-score-display {
  font-size: 4rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  text-align: center;
  margin-bottom: 0.5rem;
}

.risk-score-value {
  color: #667eea;
  text-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
}

.risk-score-separator {
  color: #6c757d;
  margin: 0 0.5rem;
}

.risk-score-max {
  color: #6c757d;
}

.risk-metric-info {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-left: 4px solid #667eea;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  max-width: 800px;
  margin: 0 auto;
}

.risk-metric-info i {
  color: #667eea;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.risk-metric-loading,
.risk-metric-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.risk-metric-loading .spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

.risk-metric-error i {
  font-size: 2.5rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .risk-metric-header {
    padding: 1.25rem 1.5rem;
  }

  .risk-metric-title {
    font-size: 1.25rem;
  }

  .risk-metric-body {
    padding: 2rem 1.5rem;
  }

  .risk-score-display {
    font-size: 3rem;
  }

  .risk-metric-info {
    font-size: 0.8125rem;
    padding: 0.875rem 1rem;
  }
}

@media (max-width: 480px) {
  .risk-metric-header {
    padding: 1rem;
  }

  .risk-metric-title {
    font-size: 1.125rem;
  }

  .risk-metric-body {
    padding: 1.5rem 1rem;
  }

  .risk-score-display {
    font-size: 2.5rem;
  }

  .risk-score-separator {
    margin: 0 0.25rem;
  }

  .risk-metric-info {
    font-size: 0.75rem;
    padding: 0.75rem;
  }
}
</style>
